import { useCallback, useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
    ApiCallError, getDocumentConfig, saveDocumentConfig, checkDocumentTemplate,
} from '@/api/client';
import { AlertTriangle } from 'lucide-react';
import type { DocumentConfigDto, TemplateCheckDto } from '@/api/types';
import { testSharePointFolder, type SharePointTestResult } from '@/api/client';

/**
 * DOCUMENT RULES, per facility (migrations 145 + 146).
 *
 * ⭐ THE PREVIEW IS THE POINT OF THIS SCREEN. A filename template is unreadable
 * until you see what it produces — nobody types
 * `{facility}-{appointment}-{doctype}-{yyyy}{MM}{dd}-{seq}` and pictures
 * `BRIGHTON-A-1042-BOL-20260819-01.pdf`. The preview comes from the SERVER,
 * rendered by the same function that names the real thing, so it cannot drift
 * into being confidently wrong.
 *
 * ⛔ THERE IS NO USERNAME OR PASSWORD FIELD HERE, AND THERE MUST NEVER BE ONE.
 * The scan folder is read by the BROWSER, using the signed-in Windows session's
 * own credentials — see the note rendered next to the folder field, which says
 * so to the person configuring it rather than only to whoever reads the code.
 */

interface Props {
    getToken: () => Promise<string>;
    // ⚠️ CAPITALISED, matching `lookups.facilities` and CheckInConfigAdmin. A
    // lower-case shape here compiles in isolation and fails the moment
    // SettingsDialog passes the real thing.
    facilities: { FacilityCode: string; DisplayName: string }[];
    /**
     * ⭐ THE FACILITY THE USER IS ACTUALLY LOOKING AT, not the first in the list.
     *
     * ⛔ THIS TAB USED TO DEFAULT TO facilities[0] AND IT COST AN AFTERNOON. Pete,
     * 2026-08-25, configured SharePoint here while his calendar was on Brighton,
     * pressed Save, watched Test connection go green on all four steps — and the
     * Paperwork panel kept insisting the site read from a local folder. It was right:
     * the config had been written against a DIFFERENT facility, and Brighton had no
     * row at all.
     *
     * ⚠️ AND NOTHING COULD HAVE TOLD HIM. `resolveDocumentConfig` never throws and
     * never returns null — a facility with no row silently gets the shipped defaults,
     * which say LocalFolder. That is the right behaviour for a reader and a terrible
     * one for a diagnosis.
     *
     * Optional, so a caller with no current facility still compiles and falls back to
     * the old behaviour.
     */
    initialFacilityCode?: string | null;
}

const CASES: { value: 'upper' | 'lower' | 'asis'; label: string }[] = [
    { value: 'upper', label: 'UPPER CASE' },
    { value: 'lower', label: 'lower case' },
    { value: 'asis', label: 'As typed' },
];

export default function DocumentConfigAdmin({ getToken, facilities, initialFacilityCode }: Props) {
    // ⚠️ Guarded against a stale id: if the caller's facility is not in the list this
    // falls back rather than selecting nothing and loading an empty screen.
    const [facilityCode, setFacilityCode] = useState(
        (initialFacilityCode && facilities.some(f => f.FacilityCode === initialFacilityCode)
            ? initialFacilityCode
            : facilities[0]?.FacilityCode) ?? '',
    );
    const [config, setConfig] = useState<DocumentConfigDto | null>(null);
    /**
     * ---- ⛔ WHAT THE SERVER LAST SAID THE SCAN SOURCE WAS -----------------------
     *
     * Pete, 2026-08-25: *"It shows that its not set when you click on the Local /
     * network folder in Documents config screen?"*
     *
     * ⚠️ HE CLICKED IT TO LOOK, AND CLICKING IT IS A CHANGE. The two buttons are a
     * picker, so pressing the unselected one switches the form and reveals an empty
     * folder field — which reads as "this is broken" when it actually means "this
     * site does not use that source, so nobody ever filled it in".
     *
     * ⛔ AND THE REAL HAZARD IS THE NEXT CLICK. Save at that point takes a working
     * SharePoint facility off SharePoint: the paperwork tray disappears, the browser
     * folder picker takes its place, and nothing errors — the single most expensive
     * shape of bug this codebase keeps re-learning.
     *
     * ⭐ SO THE SAVED VALUE IS KEPT AND COMPARED. The warning below fires the instant
     * the form diverges from it, says what Save would actually do, and offers to put
     * it back — no dialog, nothing blocked, and looking stays free.
     */
    const [savedSourceType, setSavedSourceType] =
        useState<DocumentConfigDto['scanSourceType'] | null>(null);
    const [tokens, setTokens] = useState<string[]>([]);
    const [check, setCheck] = useState<TemplateCheckDto | null>(null);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [saved, setSaved] = useState(false);

    const load = useCallback(async () => {
        if (!facilityCode) return;
        setLoading(true);
        setError(null);
        setSaved(false);
        try {
            const res = await getDocumentConfig(await getToken(), facilityCode);
            setConfig(res.config);
            setSavedSourceType(res.config.scanSourceType);
            setTokens(res.tokens);
        } catch (err) {
            setError(err instanceof ApiCallError ? err.details.error : 'Could not load the document rules.');
        } finally {
            setLoading(false);
        }
    }, [facilityCode, getToken]);

    useEffect(() => { void load(); }, [load]);

    /**
     * ⚠️ DEBOUNCED, because this runs on every keystroke in the template field.
     * Without it a scheduler typing a 40-character template fires forty
     * round-trips and watches the preview flicker through nonsense.
     */
    const debounce = useRef<number | null>(null);
    useEffect(() => {
        if (!config) return;
        if (debounce.current) window.clearTimeout(debounce.current);
        debounce.current = window.setTimeout(() => {
            void (async () => {
                try {
                    setCheck(await checkDocumentTemplate(
                        await getToken(), config.fileNameTemplate, config.fileNameCase));
                } catch {
                    // A failed preview must not block saving — the server
                    // validates again on save, which is the check that counts.
                    setCheck(null);
                }
            })();
        }, 350);
        return () => { if (debounce.current) window.clearTimeout(debounce.current); };
    }, [config?.fileNameTemplate, config?.fileNameCase, getToken, config]);

    const set = <K extends keyof DocumentConfigDto>(key: K, value: DocumentConfigDto[K]) => {
        setConfig(c => (c ? { ...c, [key]: value } : c));
        setSaved(false);
    };

    async function save() {
        if (!config) return;
        setSaving(true);
        setError(null);
        try {
            const res = await saveDocumentConfig(await getToken(), {
                facilityCode: config.facilityCode,
                fileNameTemplate: config.fileNameTemplate,
                fileNameCase: config.fileNameCase,
                allowedExtensions: config.allowedExtensions,
                maxFileMb: config.maxFileMb,
                documentTypes: config.documentTypes,
                requirePaperworkOutbound: config.requirePaperworkOutbound,
                retentionDays: config.retentionDays,
                scanFolderHint: config.scanFolderHint,
                scanSourceType: config.scanSourceType,
                spHostname: config.spHostname,
                spSitePath: config.spSitePath,
                spFolderPath: config.spFolderPath,
                spDriveId: config.spDriveId,
                spItemId: config.spItemId,
                enableSignature: config.enableSignature,
                signatureTitle: config.signatureTitle,
                signatureTemplate: config.signatureTemplate,
                signatureNameSource: config.signatureNameSource,
                signatureFixedName: config.signatureFixedName,
                signatureFontSize: config.signatureFontSize,
                signedFileSuffix: config.signedFileSuffix,
            });
            setConfig(res.config);
            // ⚠️ The warning is about UNSAVED divergence. Once saved, this IS the value.
            setSavedSourceType(res.config.scanSourceType);
            setSaved(true);
        } catch (err) {
            setError(err instanceof ApiCallError ? err.details.error : 'Could not save.');
        } finally {
            setSaving(false);
        }
    }

    if (!config) {
        return <div className="p-3 text-sm text-muted-foreground">{loading ? 'Loading…' : (error ?? '')}</div>;
    }

    return (
        <div className="space-y-4 p-1">
            <div className="flex items-end gap-3">
                <div className="space-y-1">
                    <Label htmlFor="doc-facility">Facility</Label>
                    <select
                        id="doc-facility"
                        value={facilityCode}
                        onChange={(e) => setFacilityCode(e.target.value)}
                        className="h-9 rounded-md border bg-background px-2 text-sm"
                    >
                        {facilities.map(f => (
                            <option key={f.FacilityCode} value={f.FacilityCode}>
                                {f.DisplayName ? `${f.FacilityCode} — ${f.DisplayName}` : f.FacilityCode}
                            </option>
                        ))}
                    </select>
                </div>
                {config.isDefaultShipped && (
                    // ⚠️ Said out loud. Otherwise a scheduler edits fields, never
                    // presses Save, and believes the site is configured.
                    <div className="pb-2 text-xs text-amber-700">
                        This facility has no rules of its own yet — these are the shipped
                        defaults. Press Save to make them this site's.
                    </div>
                )}
            </div>

            {/* ---- naming ------------------------------------------------- */}
            <section className="space-y-2 rounded border p-3">
                <h3 className="text-sm font-medium">What scanned documents are called</h3>

                <div className="space-y-1">
                    <Label htmlFor="doc-template">Filename template</Label>
                    <Input
                        id="doc-template"
                        value={config.fileNameTemplate}
                        onChange={(e) => set('fileNameTemplate', e.target.value)}
                        className="font-mono text-xs"
                    />
                    <div className="flex flex-wrap gap-1 pt-0.5">
                        {tokens.map(tk => (
                            <button
                                key={tk}
                                type="button"
                                // Clicking appends rather than replaces — a token
                                // list you have to retype by hand is a list nobody
                                // uses and a source of the exact typos the server
                                // then rejects.
                                onClick={() => set('fileNameTemplate', `${config.fileNameTemplate}{${tk}}`)}
                                className="rounded border bg-muted px-1.5 py-0.5 font-mono text-[10px] hover:bg-accent"
                            >
                                {'{' + tk + '}'}
                            </button>
                        ))}
                    </div>
                </div>

                <div className="space-y-1">
                    <Label htmlFor="doc-case">Case</Label>
                    <select
                        id="doc-case"
                        value={config.fileNameCase}
                        onChange={(e) => set('fileNameCase', e.target.value as DocumentConfigDto['fileNameCase'])}
                        className="h-9 rounded-md border bg-background px-2 text-sm"
                    >
                        {CASES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                    </select>
                </div>

                {/* ---- the preview ---------------------------------------- */}
                {check?.problem ? (
                    <div className="rounded border border-destructive/40 bg-destructive/5 px-2 py-1 text-xs text-destructive">
                        {check.problem.message}
                    </div>
                ) : check?.preview ? (
                    <div className="rounded border bg-muted/50 px-2 py-1.5 text-xs">
                        <div className="text-muted-foreground">A filed document would be called</div>
                        <div className="font-mono font-medium">{check.preview}</div>
                        {/* ⚠️ THE SECOND EXAMPLE EARNS ITS SPACE. A scheduler meets
                            an unfiled scan within a day, and without this the
                            UNFILED placeholder looks like a bug when it appears. */}
                        <div className="mt-1 text-muted-foreground">Before it is filed to an appointment</div>
                        <div className="font-mono">{check.previewUnfiled}</div>
                    </div>
                ) : null}
            </section>

            {/* ---- the scan folder ---------------------------------------- */}
            <section className="space-y-2 rounded border p-3">
                <h3 className="text-sm font-medium">Where this site's scanner puts its files</h3>

                {/**
                  * ⭐ #150 — THE SOURCE IS A CHOICE, NOT A MIGRATION. Pete, 2026-08-24:
                  * *"it needs to map to a online, web, sharepoint folder than a local
                  * drive, but having both would be nice."* So a facility picks one and
                  * the other stays put, unused.
                  *
                  * ⛔ AND ONLY THE CHOSEN ONE IS EVER READ (arch §1R.16). The unselected
                  * block is hidden rather than merely ignored — a visible field that does
                  * nothing is how somebody spends an afternoon editing a path that has no
                  * effect.
                  */}
                <div className="flex gap-2">
                    {(['LocalFolder', 'SharePoint'] as const).map((t) => (
                        <button
                            key={t}
                            type="button"
                            onClick={() => set('scanSourceType', t)}
                            className={
                                'rounded border px-3 py-1.5 text-xs '
                                + (config.scanSourceType === t
                                    ? 'border-slate-900 bg-slate-900 text-white'
                                    : 'border-slate-300 hover:bg-slate-50')
                            }
                        >
                            {t === 'LocalFolder' ? 'Local / network folder' : 'SharePoint'}
                        </button>
                    ))}
                </div>

                {/**
                  * ---- ⛔ SWITCHING SOURCE IS NOT A VIEW, AND IT MUST SAY SO -------
                  *
                  * ⚠️ THE FOLDER FIELD BEING EMPTY IS CORRECT AND LOOKS BROKEN. A site
                  * on SharePoint never filled in a local folder, so clicking across to
                  * look at it shows a blank box — which reads as a missing setting
                  * rather than as an unused one.
                  *
                  * ⛔ AND SAVE FROM THERE IS SILENT AND EXPENSIVE. It takes a working
                  * facility off SharePoint: the paperwork tray vanishes, the browser
                  * folder picker replaces it, and nothing errors.
                  *
                  * ⭐ NOT A DIALOG. Blocking the click would make looking cost a
                  * confirmation, and the click itself is harmless — it is SAVE that
                  * does something. So this states the consequence and offers the way
                  * back, and stays out of the way otherwise.
                  */}
                {savedSourceType !== null && config.scanSourceType !== savedSourceType && (
                    <div className="flex items-start gap-2 rounded border border-amber-400 bg-amber-50 px-2 py-1.5
                                    text-[11px] text-amber-900">
                        <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                        <span className="flex-1">
                            <strong>This is a change, not a view.</strong>{' '}
                            {savedSourceType === 'SharePoint'
                                ? 'This site currently reads its scans from SharePoint. Saving now '
                                  + 'switches it to a local folder: the Scanned Paperwork tray stops showing '
                                  + 'scanned documents and schedulers pick the folder in their browser '
                                  + 'instead. The SharePoint settings are kept, not deleted.'
                                : 'This site currently uses a local folder. Saving now switches it to '
                                  + 'SharePoint, and the Scanned Paperwork tray will read that folder instead.'}
                        </span>
                        <button
                            type="button"
                            onClick={() => set('scanSourceType', savedSourceType)}
                            className="shrink-0 rounded border border-amber-500 px-1.5 py-0.5 font-medium
                                       hover:bg-amber-100"
                        >
                            Put it back
                        </button>
                    </div>
                )}

                {config.scanSourceType === 'SharePoint' && (
                    <SharePointFields
                        config={config}
                        set={set}
                        getToken={getToken}
                        facilityCode={facilityCode}
                        facilityName={facilities.find(f => f.FacilityCode === facilityCode)?.DisplayName ?? facilityCode}
                    />
                )}

                <div className={config.scanSourceType === 'SharePoint' ? 'hidden' : 'space-y-1'}>
                    <Label htmlFor="doc-folder">Scan folder</Label>
                    {/**
                      * ⭐ EMPTY BECAUSE IT IS UNUSED, NOT BECAUSE IT IS MISSING — said
                      * out loud, at the moment somebody is looking at the blank box.
                      * ⛔ AND IT ANSWERS THE OTHER HALF OF PETE'S QUESTION IN THE PLACE
                      * HE ASKED IT: dragging a file in from a PC uses no folder setting
                      * at all, so there is nothing here for that and never was.
                      */}
                    {savedSourceType === 'SharePoint' && (
                        <div className="rounded border border-slate-200 bg-slate-50 px-2 py-1.5
                                        text-[11px] text-slate-700">
                            This is <strong>blank because this site uses SharePoint</strong>, not because
                            a setting is missing — nobody ever filled it in. And it has nothing to do with
                            dragging files in from a PC: <strong>that path needs no setting</strong>, the
                            file goes straight into Dock from wherever the scheduler dragged it.
                        </div>
                    )}
                    <Input
                        id="doc-folder"
                        value={config.scanFolderHint ?? ''}
                        onChange={(e) => set('scanFolderHint', e.target.value || null)}
                        placeholder="\\\\srv-fs01\\scans\\brighton"
                        className="font-mono text-xs"
                    />
                    {/* ⛔ THE ANSWER TO "WHERE DO THE CREDENTIALS GO", RENDERED FOR
                        THE PERSON CONFIGURING IT — not buried in a code comment
                        where the only people who read it already know. */}
                    <div className="rounded border border-slate-200 bg-slate-50 px-2 py-1.5 text-[11px] text-slate-700">
                        This is a <strong>label, not a connection</strong>. It tells schedulers which
                        folder to choose when the browser asks for it. There is deliberately no
                        username or password here: the folder is opened by <strong>your browser,
                        using your own Windows sign-in</strong>, so each person sees exactly the
                        shares they are already entitled to and this application never holds a
                        credential. The scanner's own account for writing into the folder is set
                        on the scanner, by IT.
                    </div>
                </div>
            </section>

            {/* ---- limits ------------------------------------------------- */}
            <section className="grid grid-cols-2 gap-3 rounded border p-3">
                <div className="space-y-1">
                    <Label htmlFor="doc-ext">Allowed file types</Label>
                    <Input
                        id="doc-ext"
                        value={config.allowedExtensions}
                        onChange={(e) => set('allowedExtensions', e.target.value)}
                        className="font-mono text-xs"
                    />
                    <div className="text-[11px] text-muted-foreground">
                        An allow-list — anything not on it is refused.
                    </div>
                </div>
                <div className="space-y-1">
                    <Label htmlFor="doc-max">Largest file (MB)</Label>
                    <Input
                        id="doc-max"
                        type="number"
                        min={1}
                        max={200}
                        value={config.maxFileMb}
                        onChange={(e) => set('maxFileMb', Number(e.target.value) || 1)}
                    />
                    <div className="text-[11px] text-muted-foreground">
                        200 dpi greyscale keeps a long BOL well inside this.
                    </div>
                </div>
                <div className="col-span-2 space-y-1">
                    <Label htmlFor="doc-types">Document types</Label>
                    <Input
                        id="doc-types"
                        value={config.documentTypes}
                        onChange={(e) => set('documentTypes', e.target.value)}
                    />
                    <div className="text-[11px] text-muted-foreground">
                        The list a scheduler picks from when filing. Comma-separated.
                    </div>
                </div>
            </section>

            {/* ---- policy ------------------------------------------------- */}
            <section className="space-y-2 rounded border p-3">
                <h3 className="text-sm font-medium">Policy</h3>
                <label className="flex items-start gap-2 text-sm">
                    <input
                        type="checkbox"
                        className="mt-1"
                        checked={config.requirePaperworkOutbound}
                        onChange={(e) => set('requirePaperworkOutbound', e.target.checked)}
                    />
                    <span>
                        Require paperwork before an outbound load departs
                        <span className="block text-[11px] text-muted-foreground">
                            Adds a warning to the release check. Like every other release warning it
                            can be signed off, and the sign-off is recorded on the appointment.
                        </span>
                    </span>
                </label>

                <div className="space-y-1">
                    <Label htmlFor="doc-retention">Keep documents for (days)</Label>
                    <Input
                        id="doc-retention"
                        type="number"
                        min={1}
                        value={config.retentionDays ?? ''}
                        placeholder="blank = keep for ever"
                        onChange={(e) => set('retentionDays',
                            e.target.value.trim() === '' ? null : Number(e.target.value))}
                        className="w-48"
                    />
                    {/* ⚠️ The consequence stated where the decision is made. */}
                    <div className="text-[11px] text-amber-700">
                        Leave blank until retention has been agreed. Anything typed here starts
                        permanently deleting the scanned file after that many days — freight claims
                        run for years, and the record of the document survives but the document
                        itself does not.
                    </div>
                </div>
            </section>

            {/* ================= the load-received stamp (#110) ================ */}
            <section className="space-y-3 rounded border p-3">
                <div className="flex items-start justify-between gap-3">
                    <div>
                        <h3 className="text-sm font-semibold">Load-received stamp</h3>
                        {/* ⛔ WHAT THIS IS AND IS NOT, said to the admin configuring it
                            rather than left for them to assume. Pete: "this is really not
                            an official legal signature other than to sign off to the
                            carrier that the appointment was received." Somebody wording this
                            field needs to know that before they type into it. */}
                        <p className="mt-1 max-w-prose text-[11px] text-muted-foreground">
                            Stamps the scan with who received the appointment and when, and saves it as a
                            second document beside the original — so paperwork does not have to go
                            back across the yard and through the scanner a second time.{' '}
                            <span className="text-amber-700">
                                This is not a legal signature and must not be worded as one. It says
                                the appointment was received; it says nothing about its condition or count,
                                because the application does not know those things.
                            </span>
                        </p>
                    </div>
                    <label className="flex shrink-0 items-center gap-2 text-xs">
                        <input
                            type="checkbox"
                            checked={config.enableSignature}
                            onChange={(e) => set('enableSignature', e.target.checked)}
                        />
                        Offer the stamp
                    </label>
                </div>

                {config.enableSignature && (
                    <div className="space-y-3 border-t pt-3">
                        <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-1">
                                <Label htmlFor="sig-title">Heading line</Label>
                                <Input
                                    id="sig-title"
                                    value={config.signatureTitle}
                                    maxLength={80}
                                    onChange={(e) => set('signatureTitle', e.target.value)}
                                />
                            </div>
                            <div className="space-y-1">
                                <Label htmlFor="sig-suffix">Signed copy filename suffix</Label>
                                <Input
                                    id="sig-suffix"
                                    value={config.signedFileSuffix}
                                    maxLength={40}
                                    onChange={(e) => set('signedFileSuffix', e.target.value)}
                                />
                                <div className="text-[11px] text-muted-foreground">
                                    Added before the extension, so BOL.pdf becomes
                                    BOL{config.signedFileSuffix || '-SIGNED'}.pdf.
                                </div>
                            </div>
                        </div>

                        <div className="space-y-1">
                            <Label htmlFor="sig-template">Wording</Label>
                            <textarea
                                id="sig-template"
                                rows={3}
                                value={config.signatureTemplate}
                                maxLength={200}
                                onChange={(e) => set('signatureTemplate', e.target.value)}
                                className="w-full rounded border bg-background px-2 py-1 font-mono text-xs"
                            />
                            <div className="text-[11px] text-muted-foreground">
                                Up to four lines. Tokens:{' '}
                                <code>{'{name} {title} {facility} {appointment} {date} {time} {datetime}'}</code>.
                                {/* ⚠️ The refusal explained where somebody can act on it. */}
                                {' '}A token that is not on this list is refused when you save,
                                rather than printed as a blank — a stamp missing the only thing
                                that identifies it is worse than no stamp, because it looks
                                like one.
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-1">
                                <Label htmlFor="sig-source">Whose name is printed</Label>
                                <select
                                    id="sig-source"
                                    value={config.signatureNameSource}
                                    onChange={(e) => set('signatureNameSource',
                                        e.target.value === 'fixed' ? 'fixed' : 'user')}
                                    className="h-9 w-full rounded border bg-background px-2 text-sm"
                                >
                                    <option value="user">Whoever is signed in</option>
                                    <option value="fixed">One name for this site</option>
                                </select>
                                {/* ⛔ The boundary, stated. There is no third option where the
                                    person signing types a name, and that is deliberate. */}
                                <div className="text-[11px] text-muted-foreground">
                                    Taken from the signed-in account on the server. Nobody can type a
                                    name at the moment of stamping, so a scheduler cannot put a
                                    colleague&apos;s name on a carrier&apos;s document.
                                </div>
                            </div>
                            <div className="space-y-1">
                                <Label htmlFor="sig-fixed">Site name</Label>
                                <Input
                                    id="sig-fixed"
                                    value={config.signatureFixedName ?? ''}
                                    maxLength={120}
                                    disabled={config.signatureNameSource !== 'fixed'}
                                    placeholder="e.g. Symbia Receiving"
                                    onChange={(e) => set('signatureFixedName', e.target.value || null)}
                                />
                                <div className="text-[11px] text-muted-foreground">
                                    Also the answer when a name derived from an email address comes
                                    out wrong — McDonald reads as Mcdonald.
                                </div>
                            </div>
                        </div>

                        <div className="space-y-1">
                            <Label htmlFor="sig-font">Text size</Label>
                            <Input
                                id="sig-font"
                                type="number"
                                min={6}
                                max={28}
                                value={config.signatureFontSize}
                                onChange={(e) => set('signatureFontSize', Number(e.target.value) || 10)}
                                className="w-24"
                            />
                        </div>
                    </div>
                )}
            </section>

            {error && (
                <div className="rounded border border-destructive/40 bg-destructive/5 px-2 py-1 text-sm text-destructive">
                    {error}
                </div>
            )}

            <div className="flex items-center gap-3">
                <Button size="sm" onClick={() => void save()} disabled={saving || !!check?.problem}>
                    {saving ? 'Saving…' : 'Save'}
                </Button>
                {saved && <span className="text-xs text-emerald-700">Saved.</span>}
                {check?.problem && (
                    <span className="text-xs text-muted-foreground">
                        Fix the template before saving.
                    </span>
                )}
            </div>
        </div>
    );
}

/**
 * ⭐ #150 — THE SHAREPOINT FIELDS, AND THE BUTTON THAT PROVES THEM.
 *
 * ⛔ THE TEST BUTTON IS NOT A NICETY. Dock reaches SharePoint with the Function App's
 * MANAGED IDENTITY, which by design cannot be impersonated from anybody's laptop — so
 * "can we actually read the Brighton folder" is a question ONLY code inside the
 * Function App can answer. Without this button the answer needs browser devtools and a
 * copied bearer token, which is not a thing to ask of the person who will actually be
 * configuring this at 6am when scans stop appearing.
 *
 * ⚠️ AND IT TESTS THE FORM, NOT THE SAVED ROW. You find out the path is wrong BEFORE
 * committing it, not after.
 */
function SharePointFields({
    config, set, getToken, facilityCode, facilityName,
}: {
    config: DocumentConfigDto;
    set: <K extends keyof DocumentConfigDto>(key: K, value: DocumentConfigDto[K]) => void;
    getToken: () => Promise<string>;
    /**
     * ⭐ WHICH FACILITY THIS IS FOR — carried in purely so the test can SAY it.
     *
     * ⛔ THE TEST IS FACILITY-BLIND AND THAT MISLED PETE, 2026-08-25: *"why did the
     * test connection work on the Brighton folder for BB2 - it shouldn't have
     * connected on the test."* It POSTs a hostname, a site path and a folder path and
     * asks Graph whether they resolve. They did — they were Brighton's. Nothing in the
     * request or the four green ticks mentioned which facility was being configured,
     * so a perfect result proved nothing about the site on screen.
     *
     * ⚠️ THE TEST ITSELF STAYS BLIND, ON PURPOSE. It answers "can Dock reach this
     * folder", which is the question worth asking and the one that has to work before
     * anything else does. The fix is to NAME the facility around it, not to make the
     * probe refuse folders whose names we do not like.
     */
    facilityCode: string;
    facilityName: string;
}) {
    const [testing, setTesting] = useState(false);
    const [result, setResult] = useState<SharePointTestResult | null>(null);

    const canTest = !!(config.spHostname && config.spSitePath && config.spFolderPath);

    /**
     * ⚠️ ADVISORY ONLY, AND DELIBERATELY WEAK. One folder per facility is a CONVENTION
     * (see the note below), not a rule the system enforces — a site may legitimately
     * name its folder something else entirely. So this never blocks a save and never
     * blocks a test; it just says out loud that the last segment of the path does not
     * look like the facility being configured, which is exactly the mistake that cost
     * an afternoon.
     */
    const lastSegment = (config.spFolderPath ?? '').split('/').filter(Boolean).pop() ?? '';
    const folderLooksWrong = Boolean(
        lastSegment
        && lastSegment.toLowerCase() !== facilityCode.toLowerCase()
        && lastSegment.toLowerCase() !== facilityName.toLowerCase(),
    );

    async function runTest() {
        setTesting(true);
        setResult(null);
        try {
            setResult(await testSharePointFolder(
                await getToken(),
                config.spHostname ?? '', config.spSitePath ?? '', config.spFolderPath ?? '',
            ));
        } catch (err) {
            /**
             * ⚠️ EVEN A THROWN ERROR IS RENDERED AS A STEP, so the panel always looks the
             * same. A screen that shows a tidy checklist on failure and a red blob on a
             * different kind of failure teaches nobody where to look.
             */
            setResult({
                ok: false,
                steps: [{ step: 'Call the server', ok: false, detail: err instanceof Error ? err.message : String(err) }],
                sampleFiles: [],
            });
        } finally {
            setTesting(false);
        }
    }

    return (
        <div className="space-y-2 rounded border border-slate-200 bg-slate-50 p-2">
            <div className="grid gap-2 sm:grid-cols-2">
                <div className="space-y-1">
                    <Label htmlFor="sp-host">Site address</Label>
                    <Input
                        id="sp-host"
                        value={config.spHostname ?? ''}
                        onChange={(e) => set('spHostname', e.target.value || null)}
                        placeholder="symbia.sharepoint.com"
                        className="font-mono text-xs"
                    />
                </div>
                <div className="space-y-1">
                    <Label htmlFor="sp-site">Site path</Label>
                    <Input
                        id="sp-site"
                        value={config.spSitePath ?? ''}
                        onChange={(e) => set('spSitePath', e.target.value || null)}
                        placeholder="/sites/Operations"
                        className="font-mono text-xs"
                    />
                    {/**
                      * ⛔ SAYING WHERE IT STOPS, because "Site path" reads as "the path
                      * to the thing" and the thing people are looking at is a folder.
                      * Pete put /sites/Operations/Dock Scheduler Scans/ here on
                      * 2026-08-25 — an entirely reasonable reading — and the spaces
                      * produced "fetch failed", which points at nothing.
                      */}
                    <p className="text-[11px] text-muted-foreground">
                        The site only — stop after the site name. Folders go below.
                    </p>
                </div>
            </div>

            <div className="space-y-1">
                <Label htmlFor="sp-folder">Folder for this facility</Label>
                <Input
                    id="sp-folder"
                    value={config.spFolderPath ?? ''}
                    onChange={(e) => set('spFolderPath', e.target.value || null)}
                    placeholder="Dock Scheduler Scans/Brighton"
                    className="font-mono text-xs"
                />
                {/**
                  * ⛔ THE PLACEHOLDER USED TO READ "Shared Documents/Dock Scheduler
                  * Scans/Brighton" AND THAT WAS WRONG. Graph addresses folders relative
                  * to the document library's ROOT, and that root IS "Shared Documents"
                  * (shown as "Documents" in SharePoint) — so including it looks for a
                  * folder literally named "Shared Documents" inside the library.
                  *
                  * ⚠️ It had never been caught because every attempt to reach the folder
                  * step had failed earlier, on the site lookup or on a 403.
                  */}
                <p className="text-[11px] text-muted-foreground">
                    Counted from the <strong>Documents</strong> library, which is where SharePoint
                    starts you — so leave &ldquo;Documents&rdquo; / &ldquo;Shared Documents&rdquo;
                    out. In SharePoint the breadcrumb <span className="font-mono">Documents ›
                    Dock Scheduler Scans › Brighton</span> is written{' '}
                    <span className="font-mono">Dock Scheduler Scans/Brighton</span> here.
                </p>
                {/**
                  * ⛔ THE "WHERE DOES THE PASSWORD GO" ANSWER, RENDERED FOR THE PERSON
                  * CONFIGURING IT — the same courtesy the local-folder block already pays.
                  * Pete asked exactly this question, so somebody else will.
                  */}
                <div className="rounded border border-slate-200 bg-white px-2 py-1.5 text-[11px] text-slate-700">
                    There is deliberately <strong>no username or password here</strong>. Dock signs in
                    to SharePoint as itself, using the identity Azure gives this application — so
                    there is no credential to store, expire or rotate. Its access is limited to the
                    one site an administrator granted; it cannot see anything else in SharePoint.
                    <strong> One folder per facility</strong> under a shared parent is the intended
                    shape.
                </div>
            </div>

            {/**
              * ⛔ THE MISMATCH SAID OUT LOUD, BEFORE THE TEST IS EVEN PRESSED.
              * Pete configured Brighton's folder while the facility selector was on a
              * different site; the test went green because the FOLDER was reachable,
              * which was true and useless.
              * ⚠️ A CAUTION, NOT A BLOCK — one folder per facility is a convention and
              * a site is allowed to break it.
              */}
            {folderLooksWrong && (
                <div className="rounded border border-amber-400 bg-amber-50 px-2 py-1.5 text-[11px] text-amber-900">
                    You are configuring <strong>{facilityName}</strong>, but the folder ends in{' '}
                    <span className="font-mono">{lastSegment}</span>. One folder per facility is the
                    intended shape — check the facility picker at the top of this tab before saving.
                </div>
            )}

            <div className="flex items-center gap-2">
                <button
                    type="button"
                    onClick={() => void runTest()}
                    disabled={!canTest || testing}
                    className="rounded border border-slate-300 px-2 py-1 text-sm hover:bg-white disabled:opacity-50"
                >
                    {testing ? 'Testing…' : `Test connection for ${facilityName}`}
                </button>
                {!canTest && (
                    <span className="text-[11px] text-slate-500">
                        Fill in all three fields to test.
                    </span>
                )}
            </div>

            {result && (
                <div className="space-y-1 rounded border bg-white p-2 text-xs">
                    {/**
                      * ⭐ EVERY STEP IS SHOWN, INCLUDING THE ONES THAT PASSED. There are
                      * four ways this breaks and they have four different fixes; a single
                      * pass/fail would make the next person guess. Seeing "site ✓, library
                      * ✗" is the whole diagnosis.
                      */}
                    {result.steps.map((st, i) => (
                        <div key={i} className="flex gap-2">
                            <span className={st.ok ? 'text-emerald-600' : 'text-red-600'}>
                                {st.ok ? '✓' : '✗'}
                            </span>
                            <span className="font-medium">{st.step}</span>
                            <span className="text-slate-600">{st.detail}</span>
                        </div>
                    ))}
                    {result.ok && (
                        <div className="pt-1 text-slate-600">
                            {result.sampleFiles.length === 0
                                ? 'The folder is reachable and currently empty.'
                                : <>Newest files: {result.sampleFiles.join(', ')}</>}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
